/**
 * Regional format (2026-09-29): date, time and number format, currency and
 * timezone. The settings were already stored (/preferences/localization) and
 * applied everywhere through LocalizationContext, but nothing let anyone
 * change them — the old section showed hardcoded values.
 */
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { useLocalization } from '../../contexts/LocalizationContext'
import type { LocalizationPrefs } from '../../services/localization'
import { apiErrorMessage } from '../../utils/apiError'

const CURRENCIES = ['USD', 'EUR', 'GBP', 'AUD', 'CAD', 'CHF', 'JPY', 'CNY', 'INR', 'SGD', 'MYR', 'AED', 'SAR', 'PKR', 'ZAR', 'NZD', 'SEK', 'NOK', 'DKK', 'BRL', 'MXN']
const FALLBACK_ZONES = ['UTC', 'Europe/London', 'Europe/Berlin', 'Europe/Paris', 'Europe/Zurich', 'America/New_York',
  'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Toronto', 'Asia/Dubai', 'Asia/Karachi',
  'Asia/Kolkata', 'Asia/Singapore', 'Asia/Kuala_Lumpur', 'Asia/Tokyo', 'Australia/Sydney', 'Pacific/Auckland']

function timeZones(): string[] {
  const intl = Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
  try {
    const all = intl.supportedValuesOf?.('timeZone')
    if (all && all.length) return all.includes('UTC') ? all : ['UTC', ...all]
  } catch {
    /* older browsers */
  }
  return FALLBACK_ZONES
}

export function RegionalFormatSection() {
  const { prefs, update, formatDate, formatNumber, formatCurrency } = useLocalization()
  const [draft, setDraft] = useState<LocalizationPrefs>(prefs)
  const [saving, setSaving] = useState(false)
  useEffect(() => setDraft(prefs), [prefs])

  const dirty = JSON.stringify(draft) !== JSON.stringify(prefs)
  const save = async () => {
    setSaving(true)
    try {
      await update(draft)
      toast.success('Regional format saved.')
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not save.'))
    } finally {
      setSaving(false)
    }
  }
  const select = 'mt-1 min-h-[44px] w-full rounded-md border border-border bg-background px-3 text-sm text-foreground'
  const zones = timeZones()

  return (
    <div className="space-y-6">
      <div>
        <h1 className="mb-1 text-2xl text-foreground">Regional format</h1>
        <p className="text-muted-foreground">How dates, numbers and money appear for you across Karyva.</p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Format</CardTitle>
          <CardDescription>
            Preview: {formatDate(new Date(), { withTime: true })} · {formatNumber(1234567.891, 2)} · {formatCurrency(48250)}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <label className="text-sm text-foreground">Date format
            <select className={select} value={draft.date_format}
              onChange={(e) => setDraft({ ...draft, date_format: e.target.value as LocalizationPrefs['date_format'] })}>
              <option value="DD/MM/YYYY">31/12/2026</option>
              <option value="MM/DD/YYYY">12/31/2026</option>
              <option value="YYYY-MM-DD">2026-12-31</option>
            </select>
          </label>
          <label className="text-sm text-foreground">Time format
            <select className={select} value={draft.time_format}
              onChange={(e) => setDraft({ ...draft, time_format: e.target.value as LocalizationPrefs['time_format'] })}>
              <option value="24h">24-hour (17:30)</option>
              <option value="12h">12-hour (5:30 pm)</option>
            </select>
          </label>
          <label className="text-sm text-foreground">Number format
            <select className={select} value={draft.number_format}
              onChange={(e) => setDraft({ ...draft, number_format: e.target.value as LocalizationPrefs['number_format'] })}>
              <option value="1,234.56">1,234.56</option>
              <option value="1.234,56">1.234,56</option>
              <option value="1 234,56">1 234,56</option>
            </select>
          </label>
          <label className="text-sm text-foreground">Default currency
            <select className={select} value={draft.currency} onChange={(e) => setDraft({ ...draft, currency: e.target.value })}>
              {(CURRENCIES.includes(draft.currency) ? CURRENCIES : [draft.currency, ...CURRENCIES]).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label className="text-sm text-foreground sm:col-span-2">Timezone
            <select className={select} value={draft.timezone} onChange={(e) => setDraft({ ...draft, timezone: e.target.value })}>
              {(zones.includes(draft.timezone) ? zones : [draft.timezone, ...zones]).map((z) => <option key={z} value={z}>{z.replace(/_/g, ' ')}</option>)}
            </select>
          </label>
          <div className="sm:col-span-2">
            <Button onClick={save} disabled={!dirty || saving} className="min-h-[44px]">{saving ? 'Saving…' : 'Save'}</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
